import React, { useState } from 'react';
import axios from 'axios';
// Define API_BASE_URL directamente aquí.
import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography,
    Stack, CircularProgress, Snackbar, Alert,
    InputAdornment, Table, TableBody, TableCell, TableContainer, TableRow, Link // Añadido Link para URLs
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FingerprintIcon from '@mui/icons-material/Fingerprint'; // <-- ÍCONO AÑADIDO

// Interfaz para el resultado de la búsqueda de MD5
interface MD5LookupResult {
    hashed_value: string;
    original_subid_value: string;
    original_param_name: string;
    traffic_source_name: string;
    campaign_name: string;
    click_id: string;
}

const MD5LookupPage: React.FC = () => {
    const [hashedValue, setHashedValue] = useState<string>('');
    const [lookupResult, setLookupResult] = useState<MD5LookupResult | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    const handleLookup = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsLoading(true);
        setLookupResult(null);
        setNotification(null); // Limpiar notificaciones anteriores

        if (!hashedValue.trim()) {
            setNotification({ type: 'error', message: 'Por favor, introduce un valor MD5 para buscar.' });
            setIsLoading(false);
            return;
        }

        try {
            const response = await axios.get(`${API_BASE_URL}/resolve-subid-hash?hashed_value=${hashedValue}`);
            setLookupResult(response.data);
            setNotification({ type: 'success', message: 'Búsqueda exitosa!' });
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || 'Error desconocido durante la búsqueda.';
            setNotification({ type: 'error', message: `Error: ${errorMessage}` });
            console.error("Error al buscar MD5:", err);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper para renderizar valores de forma consistente (similar al de ClickInfoPage)
    const renderValue = (value: any, type?: 'url'): React.ReactNode => {
        if (value === null || value === undefined || value === '') {
            return <Typography variant="body2" color="text.secondary">N/A</Typography>;
        }

        switch (type) {
            case 'url':
                return (
                    <Link href={value} target="_blank" rel="noopener noreferrer" variant="body2">
                        {value.length > 50 ? `${value.substring(0, 47)}...` : value}
                    </Link>
                );
            default:
                return <Typography variant="body2">{String(value)}</Typography>;
        }
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            {/* Snackbar para notificaciones */}
            <Snackbar
                open={!!notification}
                autoHideDuration={6000}
                onClose={() => setNotification(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>
                    {notification?.message}
                </Alert>
            </Snackbar>

            {/* --- CAMBIO: Título actualizado --- */}
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <FingerprintIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Buscador de SubID por MD5
            </Typography>

            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
                <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                    Buscar Valor Original
                </Typography>
                <Box component="form" onSubmit={handleLookup} noValidate>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                        <TextField
                            fullWidth
                            label="Introduce el Hash MD5"
                            id="hashed_value_input"
                            value={hashedValue}
                            onChange={(e) => setHashedValue(e.target.value)}
                            placeholder="ej. d27884252a33786a306a52612857200d"
                            required
                            variant="outlined"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button type="submit" variant="contained" disabled={isLoading} sx={{ minWidth: { xs: '100%', sm: '150px' }, height: '56px' }}>
                            {isLoading ? <CircularProgress size={24} /> : 'Buscar Original'}
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            {lookupResult && (
                <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
                    <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                        Resultado de la Búsqueda
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell component="th" width="40%">Hash Buscado:</TableCell>
                                    <TableCell>{renderValue(lookupResult.hashed_value)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th">Valor Original:</TableCell>
                                    <TableCell>{renderValue(lookupResult.original_subid_value)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th">Nombre del Parámetro Original:</TableCell>
                                    <TableCell>{renderValue(lookupResult.original_param_name)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th">ID de Clic:</TableCell>
                                    <TableCell>{renderValue(lookupResult.click_id)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th">Campaña:</TableCell>
                                    <TableCell>{renderValue(lookupResult.campaign_name)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th">Fuente de Tráfico:</TableCell>
                                    <TableCell>{renderValue(lookupResult.traffic_source_name)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Container>
    );
};

export default MD5LookupPage;